/**
 * Sistema de diseño global para Biavent (Web)
 * Define colores, tipografía, sombras, radios y espaciados consistentes en toda la aplicación
 */

// ============================================
// COLORES (Brand: Navy + Dorado - docs/ux/brand-guide.md)
// ============================================

// Brand primarios
export const NavyPrimary = '#152238';
export const NavyDark = '#0f1a2a';
export const GoldAccent = '#C9A84C';
export const GoldDark = '#B8983D';

// Legacy: teal-* mapeados a dorado (compatibilidad useThemeColor('teal'))
export const TealColor = GoldAccent;
export const TealDark = GoldDark;
export const TealVeryDark = '#A68A36';
export const TealLightBackground = 'rgba(201, 168, 76, 0.12)';

// Colores Neutros (brand guide)
export const White = '#FFFFFF';
export const LightGrayBackground = '#F8F9FC';
export const LightGrayBackgroundAlt = '#F8F9FC';
export const GrayBorder = '#ddd';
export const GrayText = '#6B7280';
export const GrayPlaceholder = '#999';
export const BlackText = '#1a1a1a';

// Colores de Estado (según Design System)
export const ErrorRed = '#ff3b30';
export const SuccessGreen = '#4CAF50';
export const WarningOrange = '#FF9800';
export const InfoBlue = '#2196F3';

// Colores legacy (mantener para compatibilidad)
export const DarkGrayTitle = BlackText;
export const MediumGray = GrayText;
export const LightGrayText = GrayPlaceholder;
export const DividerGray = '#e5e5e5';

// Color naranja para líneas de tiempo alternas
export const OrangeColor = '#FF6B35';

// ============================================
// COLORES PARA ONBOARDING - NIVELES DE EXPERIENCIA
// ============================================

export const ExperienceColors = {
  beginner: {
    primary: '#3B82F6',
    light: '#DBEAFE',
    dark: '#1E40AF',
    gradient: ['#DBEAFE', '#BFDBFE'],
  },
  intermediate: {
    primary: '#10B981',
    light: '#D1FAE5',
    dark: '#047857',
    gradient: ['#D1FAE5', '#A7F3D0'],
  },
  advanced: {
    primary: '#F59E0B',
    light: '#FEF3C7',
    dark: '#D97706',
    gradient: ['#FEF3C7', '#FDE68A'],
  },
  expert: {
    primary: '#8B5CF6',
    light: '#EDE9FE',
    dark: '#6D28D9',
    gradient: ['#EDE9FE', '#DDD6FE'],
  },
};

// ============================================
// COLORES PARA ONBOARDING - CATEGORÍAS
// ============================================

export const CategoryColors = {
  professional: {
    primary: '#2196F3',
    light: '#E3F2FD',
    dark: '#1976D2',
  },
  lifestyle: {
    primary: '#10B981',
    light: '#D1FAE5',
    dark: '#047857',
  },
  compatibility: {
    primary: '#2196F3',
    light: '#E3F2FD',
    dark: '#1976D2',
  },
  connection: {
    primary: '#10B981',
    light: '#D1FAE5',
    dark: '#047857',
  },
  special: {
    primary: '#F59E0B',
    light: '#FEF3C7',
    dark: '#D97706',
  },
};

// ============================================
// COLORES PARA ESTADOS DE INTERACCIÓN
// ============================================

export const InteractionColors = {
  hover: {
    background: '#F3F4F6',
    border: '#9CA3AF',
  },
  selected: {
    background: GoldAccent,
    border: GoldAccent,
    text: White,
  },
  disabled: {
    background: LightGrayBackground,
    border: DividerGray,
    text: GrayPlaceholder,
    opacity: 0.5,
  },
  focus: {
    border: GoldAccent,
    shadow: 'rgba(201, 168, 76, 0.15)',
  },
  error: {
    background: '#FEE2E2',
    border: ErrorRed,
    text: '#DC2626',
  },
  success: {
    background: '#D1FAE5',
    border: SuccessGreen,
    text: SuccessGreen,
  },
};

export const Colors = {
  light: {
    text: BlackText,
    background: White,
    tint: GoldAccent,
    icon: GrayText,
    tabIconDefault: GrayText,
    tabIconSelected: GoldAccent,
    teal: GoldAccent,
    tealDark: GoldDark,
    tealVeryDark: TealVeryDark,
    tealLightBackground: TealLightBackground,
    navy: NavyPrimary,
    navyDark: NavyDark,
    accent: GoldAccent,
    accentHover: GoldDark,
    lightGray: LightGrayBackground,
    lightGrayAlt: LightGrayBackgroundAlt,
    white: White,
    darkGrayTitle: BlackText,
    mediumGray: GrayText,
    lightGrayText: GrayPlaceholder,
    dividerGray: DividerGray,
    grayBorder: GrayBorder,
    error: ErrorRed,
    success: SuccessGreen,
    warning: WarningOrange,
    info: InfoBlue,
    blackText: BlackText,
    grayText: GrayText,
    grayPlaceholder: GrayPlaceholder,
  },
  dark: {
    text: '#f5f7fa',
    background: '#0a0e12',
    tint: GoldAccent,
    icon: '#b8c5d1',
    tabIconDefault: '#b8c5d1',
    tabIconSelected: GoldAccent,
    teal: GoldAccent,
    tealDark: GoldDark,
    tealVeryDark: TealVeryDark,
    tealLightBackground: 'rgba(201, 168, 76, 0.15)',
    navy: NavyPrimary,
    navyDark: NavyDark,
    accent: GoldAccent,
    accentHover: GoldDark,
    lightGray: '#1a1f26',
    lightGrayAlt: '#1f252d',
    white: '#0a0e12',
    darkGrayTitle: '#f5f7fa',
    mediumGray: '#b8c5d1',
    lightGrayText: '#8b98a5',
    dividerGray: 'rgba(255, 255, 255, 0.08)',
    grayBorder: 'rgba(255, 255, 255, 0.12)',
    error: '#ff6b6b',
    success: '#51cf66',
    warning: '#ffa94d',
    info: '#4dabf7',
    blackText: '#f5f7fa',
    grayText: '#b8c5d1',
    grayPlaceholder: '#8b98a5',
  },
};

// ============================================
// TIPOGRAFÍA
// ============================================

export const Typography = {
  h1: {
    fontSize: '32px',
    fontWeight: '700',
    lineHeight: '41.6px',
    color: BlackText,
  },
  h2: {
    fontSize: '24px',
    fontWeight: '700',
    lineHeight: '31.2px',
    color: BlackText,
  },
  h3: {
    fontSize: '20px',
    fontWeight: '600',
    lineHeight: '26px',
    color: BlackText,
  },
  h4: {
    fontSize: '16px',
    fontWeight: '600',
    lineHeight: '20.8px',
    color: BlackText,
  },
  bodyLarge: {
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: '24px',
    color: BlackText,
  },
  bodyRegular: {
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '21px',
    color: GrayText,
  },
  bodySmall: {
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '18px',
    color: GrayPlaceholder,
  },
  label: {
    fontSize: '12px',
    fontWeight: '500',
    lineHeight: '18px',
    color: GrayText,
  },
  caption: {
    fontSize: '11px',
    fontWeight: '400',
    lineHeight: '16.5px',
    color: GrayPlaceholder,
  },
  screenTitle: {
    fontSize: '24px',
    fontWeight: '600',
    lineHeight: '31.2px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    lineHeight: '26px',
    color: GoldAccent,
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    lineHeight: '24px',
    color: BlackText,
  },
  body: {
    fontSize: '15px',
    fontWeight: '400',
    lineHeight: '22.5px',
    color: GrayText,
  },
  secondary: {
    fontSize: '13px',
    fontWeight: '400',
    lineHeight: '19.5px',
    color: GrayPlaceholder,
  },
  small: {
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '18px',
    color: GrayPlaceholder,
  },
};

// ============================================
// SOMBRAS
// ============================================

export const Shadow = {
  subtle: '0 2px 4px rgba(0, 0, 0, 0.06)',
  card: '0 2px 8px rgba(0, 0, 0, 0.08)',
  prominent: '0 4px 12px rgba(0, 0, 0, 0.12)',
  elevated: '0 8px 16px rgba(0, 0, 0, 0.16)',
};

// ============================================
// RADIOS DE BORDE
// ============================================

export const BorderRadius = {
  input: 4,
  small: 4,
  button: 8,
  card: 12,
  cardLarge: 16,
  cardSmall: 8,
  container: 12,
  modal: 12,
  large: 16,
  medium: 12,
  buttonLarge: 24,
};

// ============================================
// ESPACIADOS
// ============================================

export const Spacing = {
  horizontal: {
    small: 16,
    medium: 20,
    large: 24,
    xl: 32,
  },
  vertical: {
    xs: 8,
    small: 16,
    medium: 24,
    large: 32,
    xl: 40,
    xxl: 48,
  },
  card: {
    padding: 16,
    paddingLarge: 20,
  },
  gap: {
    xs: 4,
    small: 8,
    medium: 12,
    large: 16,
    xl: 20,
  },
};

// ============================================
// BREAKPOINTS RESPONSIVE
// ============================================

export const Breakpoints = {
  mobile: 0,
  tablet: 768,
  desktop: 1024,
  wide: 1440,
};

export const MaxContentWidth = 1200;

// ============================================
// TRANSICIONES Y ANIMACIONES
// ============================================

export const Transitions = {
  duration: {
    fast: 100,
    medium: 150,
    normal: 200,
    slow: 300,
    slower: 400,
    slowest: 500,
  },
  easing: {
    easeInOut: 'ease-in-out',
    easeOut: 'ease-out',
    easeIn: 'ease-in',
    spring: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
};

export const AnimationTiming = {
  screenEnter: 300,
  inputFocus: 200,
  inputValidation: 300,
  chipSelection: 150,
  cardHover: 150,
  cardSelection: 200,
  checkmarkAppear: 300,
  pulseConfirmation: 400,
  staggerDelay: 80,
  rippleDuration: 300,
};

export const AnimationEasing = {
  easeOut: 'ease-out',
  easeInOut: 'ease-in-out',
  spring: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
};
